import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { X, Loader2, Check, Briefcase, Users } from "lucide-react";

import { AccountDetailsSetupMessage } from "./account-details-setup-message";
import {
  BuddyMatrixSectionHeader,
  BuddyMatrixVendorRowLabel,
} from "./buddy-matrix-ui";
import {
  CustomerTeamColumnHeader,
  CustomerTeamMeddGroupHeaderRow,
} from "./customer-team-matrix-ui";
import MappingReviewModal from "./MappingReviewModal";
import { useRole } from "./RelationshipContexts";
import {
  buildContactIdByCustomerTeamMember,
  computeCoverageMatrixStats,
  enrichCustomerTeamWithMeddpicc,
  findPairForCoverageCell,
  getCoverageMatrixDotColor,
  buildCustomerTeamMeddGroupSpans,
  sortAccountTeamMembersForDisplay,
  sortCustomerStakeholdersForDisplay,
  sortExecSponsorsForDisplay,
  resolveContactMeddpiccMatrixMeta,
  resolveCustomerMeddRoleLabel,
  resolveCustomerMemberTitle,
} from "./relationshipUtils";
import {
  buildAllowedStakeholderKeys,
  buildCoverageCellKey,
  coverageLinkMapToApiLinks,
  apiLinksToPairKeys,
  linksMapFromOverviewMappedPairs,
  resolveCoverageHighlightCell,
  toggleCoverageLinkMap,
} from "./stakeholderMappingUtils";
import { useToast } from "../../../Components/Toast";
import { useAppDispatch } from "../../../Store/Hooks";
import {
  fetchAccountCoverageBootstrap,
  saveAccountStakeholderRelationships,
} from "../../../Store/Reducers/RelationshipSlice";
import { previewMappingChange } from "../../../Store/Reducers/TeamViewsSlice";

function coverageSaveErrorMessage(err) {
  const raw =
    (typeof err === "string" && err.trim() && err) ||
    err?.message ||
    err?.response?.data?.detail ||
    err?.response?.data?.error ||
    null;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.message === "string" && parsed.message.trim()) {
        return parsed.message;
      }
    } catch {
      // plain string message
    }
    return raw;
  }
  return "Failed to save coverage map";
}

function CoverageMatrixCell({
  cellKey,
  vendorType,
  vendorId,
  customerTeamMemberId,
  contactIdByCtm,
  matchExecutiveId,
  pairs,
  checked,
  canEdit,
  onToggle,
}) {
  const pair = findPairForCoverageCell(
    pairs,
    vendorType,
    vendorId,
    customerTeamMemberId,
    contactIdByCtm,
    matchExecutiveId
  );
  const rl = pair?.rlLevel;
  const dotTitle =
    rl === null || rl === undefined ? "Mapped — not assessed" : `RL ${rl}`;

  return (
    <td className="buddy-cell-td" data-coverage-cell={cellKey}>
      <div className={`buddy-cell${checked ? " is-checked" : ""}`}>
        <input
          type="checkbox"
          className="buddy-check"
          checked={checked}
          disabled={!canEdit}
          onChange={onToggle}
          aria-label={checked ? "Mapped" : "Unmapped"}
        />
        {checked ? (
          <span
            className="buddy-rl-dot"
            style={{ background: getCoverageMatrixDotColor(rl) }}
            title={dotTitle}
            aria-hidden
          />
        ) : null}
      </div>
    </td>
  );
}

const COVERAGE_HIGHLIGHT_SCROLL_MS = 500;
const COVERAGE_HIGHLIGHT_BLINK_MS = 2400;

export function CoverageMatrixModal({
  open,
  account,
  contacts: relationshipContacts = [],
  pairs = [],
  highlightCell = null,
  onClose,
  onSaved,
}) {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const { considerMeddpiccRole } = useRole();
  const considerMeddpiccRoleEnabled = considerMeddpiccRole !== false;

  const canEdit = true;

  const accountId = account?.id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accountTeam, setAccountTeam] = useState([]);
  const [customerTeam, setCustomerTeam] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [viewContacts, setViewContacts] = useState([]);
  const [stakeholderLinks, setStakeholderLinks] = useState({});
  const [initialLinks, setInitialLinks] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewPreview, setReviewPreview] = useState(null);
  const [pendingLinks, setPendingLinks] = useState([]);
  const [pendingNewLinks, setPendingNewLinks] = useState([]);
  const highlightRanRef = useRef(null);

  const resolvedHighlightCell = useMemo(
    () => resolveCoverageHighlightCell(highlightCell, sponsors),
    [highlightCell, sponsors]
  );

  const highlightCellKey = resolvedHighlightCell
    ? buildCoverageCellKey(
        resolvedHighlightCell.vendorType,
        resolvedHighlightCell.vendorId,
        resolvedHighlightCell.customerTeamMemberId
      )
    : null;

  const loadTeams = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const bootstrap = await dispatch(
        fetchAccountCoverageBootstrap(accountId)
      ).unwrap();
      const teamCtx = bootstrap.teamCtx;
      setAccountTeam(teamCtx.accountTeam);
      setSponsors(teamCtx.sponsors);
      setCustomerTeam(teamCtx.customerTeam);
      const map = linksMapFromOverviewMappedPairs(bootstrap.overview, teamCtx);
      setViewContacts(bootstrap.contacts);
      setStakeholderLinks(map);
      setInitialLinks(map);
    } catch (err) {
      console.error("Coverage matrix load failed:", err);
      toastRef.current.error(
        err?.response?.data?.error || "Failed to load coverage data"
      );
    } finally {
      setLoading(false);
    }
  }, [accountId, dispatch]);

  useEffect(() => {
    if (!open || !accountId) return;
    highlightRanRef.current = null;
    setEditMode(false);
    loadTeams();
  }, [open, accountId, loadTeams]);

  useEffect(() => {
    if (!open) {
      highlightRanRef.current = null;
      setEditMode(false);
    }
  }, [open]);

  const displayAccountTeam = useMemo(
    () => sortAccountTeamMembersForDisplay(accountTeam),
    [accountTeam]
  );
  const displaySponsors = useMemo(
    () => sortExecSponsorsForDisplay(sponsors),
    [sponsors]
  );
  const contactsForMeddpicc = useMemo(() => {
    if (relationshipContacts?.length) return relationshipContacts;
    return (viewContacts || []).map(c => ({
      id: String(c.id),
      name: c.name,
      meddpiccRoleCanonical: c.meddpiccRole || c.meddpicc_role,
      meddpiccRole: c.meddpiccRole,
      raw: {
        legacy_customer_team_member_id:
          c.legacyCustomerTeamMemberId || c.legacy_customer_team_member_id,
        meddpicc_role: c.meddpiccRole || c.meddpicc_role,
      },
    }));
  }, [relationshipContacts, viewContacts]);

  const enrichedCustomerTeam = useMemo(
    () => enrichCustomerTeamWithMeddpicc(customerTeam, contactsForMeddpicc),
    [customerTeam, contactsForMeddpicc]
  );

  const displayCustomerTeam = useMemo(
    () =>
      sortCustomerStakeholdersForDisplay(enrichedCustomerTeam, {
        considerMeddpiccRole: considerMeddpiccRoleEnabled,
      }),
    [enrichedCustomerTeam, considerMeddpiccRoleEnabled]
  );
  const customerTeamGroupSpans = useMemo(
    () =>
      buildCustomerTeamMeddGroupSpans(displayCustomerTeam, {
        considerMeddpiccRole: considerMeddpiccRoleEnabled,
      }),
    [displayCustomerTeam, considerMeddpiccRoleEnabled]
  );

  useEffect(() => {
    if (!open || loading || !highlightCellKey) return undefined;
    if (highlightRanRef.current === highlightCellKey) return undefined;

    const runHighlight = () => {
      const root = document.getElementById("modal-buddy");
      const cell = root?.querySelector(
        `[data-coverage-cell="${highlightCellKey}"]`
      );
      if (!cell) return;
      highlightRanRef.current = highlightCellKey;
      cell.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });

      const scrollTimer = window.setTimeout(() => {
        cell.classList.remove("coverage-cell-blink");
        void cell.offsetWidth;
        cell.classList.add("coverage-cell-blink");
        window.setTimeout(() => {
          cell.classList.remove("coverage-cell-blink");
        }, COVERAGE_HIGHLIGHT_BLINK_MS);
      }, COVERAGE_HIGHLIGHT_SCROLL_MS);

      return () => window.clearTimeout(scrollTimer);
    };

    let cleanupScrollTimer;
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        cleanupScrollTimer = runHighlight();
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      if (typeof cleanupScrollTimer === "function") cleanupScrollTimer();
    };
  }, [
    open,
    loading,
    highlightCellKey,
    displayCustomerTeam.length,
    displaySponsors.length,
    displayAccountTeam.length,
  ]);

  const allowedKeys = useMemo(
    () => buildAllowedStakeholderKeys(accountTeam, sponsors, customerTeam),
    [accountTeam, sponsors, customerTeam]
  );

  const contactsForBridge = contactsForMeddpicc;

  const contactIdByCtm = useMemo(
    () => buildContactIdByCustomerTeamMember(contactsForBridge, customerTeam),
    [contactsForBridge, customerTeam]
  );

  const sponsorExecByVendorKey = useMemo(() => {
    const map = new Map();
    for (const sp of sponsors || []) {
      const execId = sp.executive_id || null;
      if (!execId) continue;
      if (sp.type === "additional") {
        map.set(`account_sponsor:${sp.id}`, execId);
      }
      map.set(`executive:${sp.id}`, execId);
      map.set(`executive:${execId}`, execId);
    }
    return map;
  }, [sponsors]);

  const disableGrid =
    customerTeam.length === 0 ||
    (accountTeam.length === 0 && !(sponsors || []).length);

  const stats = useMemo(
    () =>
      computeCoverageMatrixStats(
        stakeholderLinks,
        allowedKeys,
        pairs,
        contactIdByCtm,
        sponsorExecByVendorKey
      ),
    [
      stakeholderLinks,
      allowedKeys,
      pairs,
      contactIdByCtm,
      sponsorExecByVendorKey,
    ]
  );

  const isDirty = useMemo(() => {
    const keys = [...allowedKeys];
    return keys.some(k => !!stakeholderLinks[k] !== !!initialLinks[k]);
  }, [stakeholderLinks, initialLinks, allowedKeys]);

  const cellsEditable = canEdit && editMode && !loading && !disableGrid;

  const toggleLink = (vendorType, vendorId, customerId) => {
    if (!cellsEditable) return;
    setStakeholderLinks(prev =>
      toggleCoverageLinkMap(prev, vendorType, vendorId, customerId, sponsors)
    );
  };

  const handleClose = () => {
    setStakeholderLinks(initialLinks);
    setEditMode(false);
    onClose();
  };

  const handleCancel = () => {
    setStakeholderLinks(initialLinks);
    setEditMode(false);
  };

  const handleEnterEditMode = () => {
    if (!canEdit || loading || disableGrid) return;
    setEditMode(true);
  };

  const persistLinks = async (links, transferDecisions = []) => {
    await dispatch(
      saveAccountStakeholderRelationships({
        accountId,
        links,
        transferDecisions,
      })
    ).unwrap();
    toast.success("Coverage map saved");
    setInitialLinks({ ...stakeholderLinks });
    setEditMode(false);
    await onSaved?.();
    onClose();
  };

  const handleSave = async () => {
    if (!canEdit || !editMode || !accountId) return;
    setSaving(true);
    try {
      const links = coverageLinkMapToApiLinks(
        stakeholderLinks,
        allowedKeys,
        sponsors
      );
      const newLinks = apiLinksToPairKeys(links);
      const preview = await dispatch(
        previewMappingChange({ accountId, newLinks })
      ).unwrap();
      if (preview?.requires_review) {
        setPendingLinks(links);
        setPendingNewLinks(newLinks);
        setReviewPreview(preview);
        setReviewOpen(true);
        return;
      }
      await persistLinks(links);
    } catch (err) {
      console.error("Coverage save failed:", err);
      toast.error(coverageSaveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReviewConfirm = async transferDecisions => {
    setSaving(true);
    try {
      await persistLinks(pendingLinks, transferDecisions);
      setReviewOpen(false);
    } catch (err) {
      console.error("Coverage save failed:", err);
      toast.error(coverageSaveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const colSpan = displayCustomerTeam.length + 1;

  return (
    <section className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <article
        id="modal-buddy"
        className={`rl-prototype-skin modal-overlay flex max-h-[92vh] w-full max-w-[1100px] flex-col rounded-xl border border-gray-200 bg-white shadow-xl${
          !editMode ? " coverage-matrix--readonly" : ""
        }`}
      >
        <header className="modal-hdr shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="modal-title">
              Relationship Matrix
              {account?.name ? ` — ${account.name}` : ""}
            </h2>
            <p className="modal-sub">
              Map exec + account team members to customer contacts for
              relationship coverage
            </p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex items-start gap-2 border-b border-gray-100 px-5 py-2 text-xs text-gray-600">
          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-purple-600" />
          Check pairs where a relationship exists or can be developed. Checked
          pairs appear in the relationship matrix.
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
              Loading coverage…
            </div>
          ) : disableGrid ? (
            <div className="flex justify-center py-12">
              <div className="sc-card max-w-md text-center">
                <AccountDetailsSetupMessage
                  accountId={accountId}
                  className="text-sm text-gray-600"
                />
              </div>
            </div>
          ) : (
            <table className="buddy-tbl">
              <thead>
                <CustomerTeamMeddGroupHeaderRow
                  groupSpans={customerTeamGroupSpans}
                />
                <tr>
                  <th className="rh" aria-label="Vendor" />
                  {displayCustomerTeam.map(c => {
                    const name = c.member_name || c.name || "Contact";
                    const memberTitle = resolveCustomerMemberTitle(c);
                    const meddLabel = resolveCustomerMeddRoleLabel(c);
                    const { meddGroup } = resolveContactMeddpiccMatrixMeta(c);
                    return (
                      <th
                        key={c.id}
                        className={`c-col mcol-${meddGroup}`}
                        title={`${name}${memberTitle ? ` · ${memberTitle}` : ""}${
                          considerMeddpiccRoleEnabled && meddLabel
                            ? ` · ${meddLabel}`
                            : ""
                        }`}
                      >
                        <CustomerTeamColumnHeader
                          member={c}
                          showMeddpiccRole={considerMeddpiccRoleEnabled}
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {displaySponsors.length > 0 ? (
                  <>
                    <BuddyMatrixSectionHeader
                      icon={Users}
                      label="Exec Team"
                      count={displaySponsors.length}
                      colSpan={colSpan}
                      showPsLegend
                    />
                    {displaySponsors.map(sp => {
                      const vendorType =
                        sp.type === "additional"
                          ? "account_sponsor"
                          : "executive";
                      const vendorId = String(
                        vendorType === "account_sponsor"
                          ? sp.id
                          : sp.executive_id || sp.id
                      );
                      return (
                        <tr key={`${vendorType}-${vendorId}`}>
                          <td className="rh">
                            <BuddyMatrixVendorRowLabel
                              vendor={{ ...sp, __vendorType: vendorType }}
                            />
                          </td>
                          {displayCustomerTeam.map(c => (
                            <CoverageMatrixCell
                              key={c.id}
                              cellKey={buildCoverageCellKey(
                                vendorType,
                                vendorId,
                                c.id
                              )}
                              vendorType={vendorType}
                              vendorId={vendorId}
                              customerTeamMemberId={c.id}
                              contactIdByCtm={contactIdByCtm}
                              matchExecutiveId={
                                sponsorExecByVendorKey.get(
                                  `${vendorType}:${vendorId}`
                                ) || sp.executive_id
                              }
                              pairs={pairs}
                              checked={
                                !!stakeholderLinks[
                                  `${vendorType}:${vendorId}:${c.id}`
                                ]
                              }
                              canEdit={cellsEditable}
                              onToggle={() =>
                                toggleLink(vendorType, vendorId, c.id)
                              }
                            />
                          ))}
                        </tr>
                      );
                    })}
                  </>
                ) : null}
                {displayAccountTeam.length > 0 ? (
                  <>
                    <BuddyMatrixSectionHeader
                      icon={Briefcase}
                      label="Account Team"
                      count={displayAccountTeam.length}
                      colSpan={colSpan}
                    />
                    {displayAccountTeam.map(m => {
                      const vendorType = "account_team_member";
                      const vendorId = String(m.id);
                      return (
                        <tr key={`team-${m.id}`}>
                          <td className="rh">
                            <BuddyMatrixVendorRowLabel
                              vendor={{ ...m, __vendorType: vendorType }}
                            />
                          </td>
                          {displayCustomerTeam.map(c => (
                            <CoverageMatrixCell
                              key={c.id}
                              cellKey={buildCoverageCellKey(
                                vendorType,
                                vendorId,
                                c.id
                              )}
                              vendorType={vendorType}
                              vendorId={vendorId}
                              customerTeamMemberId={c.id}
                              contactIdByCtm={contactIdByCtm}
                              matchExecutiveId={null}
                              pairs={pairs}
                              checked={
                                !!stakeholderLinks[
                                  `${vendorType}:${vendorId}:${c.id}`
                                ]
                              }
                              canEdit={cellsEditable}
                              onToggle={() =>
                                toggleLink(vendorType, vendorId, c.id)
                              }
                            />
                          ))}
                        </tr>
                      );
                    })}
                  </>
                ) : null}
              </tbody>
            </table>
          )}
        </div>

        <footer className="buddy-footer shrink-0 border-t border-gray-200 px-5 py-3">
          <div className="buddy-legend">
            <span className="buddy-leg-item">
              <span
                className="buddy-rl-dot"
                style={{ background: "#22c55e" }}
              />
              Strong (RL 4–5)
            </span>
            <span className="buddy-leg-item">
              <span
                className="buddy-rl-dot"
                style={{ background: "#eab308" }}
              />
              Moderate (RL 2–3)
            </span>
            <span className="buddy-leg-item">
              <span
                className="buddy-rl-dot"
                style={{ background: "#ef4444" }}
              />
              Weak (RL 0–1)
            </span>
            <span className="buddy-leg-item">
              <span
                className="buddy-rl-dot"
                style={{ background: "#94a3b8" }}
              />
              Unassessed (RL ?)
            </span>
          </div>
          <div className="buddy-stats text-sm text-gray-600">
            {stats.strong} strong ·{stats.moderate} moderate ·{stats.weak} weak
            ·{stats.unassessed} unassessed ·{stats.notMapped} not mapped
          </div>
          <div className="buddy-actions">
            {editMode ? (
              <>
                <button
                  type="button"
                  className="btn btn-sec"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-pri"
                  onClick={handleSave}
                  disabled={
                    !canEdit || saving || loading || disableGrid || !isDirty
                  }
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Save Coverage Map
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-pri"
                onClick={handleEnterEditMode}
                disabled={!canEdit || saving || loading || disableGrid}
              >
                Edit Coverage Map
              </button>
            )}
          </div>
        </footer>
      </article>
      <MappingReviewModal
        open={reviewOpen}
        preview={reviewPreview}
        newLinkCandidates={[...(accountTeam || []), ...(sponsors || [])]}
        vendorLookup={[...(accountTeam || []), ...(sponsors || [])]}
        remainingCoverageLinks={pendingNewLinks}
        onClose={() => setReviewOpen(false)}
        onConfirm={handleReviewConfirm}
      />
    </section>
  );
}

export default CoverageMatrixModal;
